import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import _ from 'lodash';
import {ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { MLUnderlyingPolicyService } from 'ml-services-common-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { UnderlyingPolicy } from 'gw-capability-quoteandbind';
import { Coverage } from 'ml-components-common-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import appConfig from 'app-config';
import classNames from 'classnames';
import UnderlyingPolicyCoverageTermsComponent from '../UnderlyingPolicyCoverageTermsComponent/UnderlyingPolicyCoverageTermsComponent';

import metadata from './UnderlyingPolicyComponent.metadata.json5';
import messages from './UnderlyingPolicyComponent.messages';
import styles from './UnderlyingPolicyComponent.module.scss';

const getContactByTypeCode = (potentialContacts, typeCodeObject) => {
    if (typeCodeObject) {
        const contacts = potentialContacts.filter((contact) => contact.ulRole === typeCodeObject.code);
        if (contacts.length > 0) {
            return contacts[0];
        }
    }
    return undefined;
};

const getPolicyNumber = (value) => {
    if (value === 'Policy is not listed') {
        return undefined;
    }
    return value;
};

function UnderlyingPolicyComponent(props) {
    const {
        id,
        underlyingPolicyVM,
        quoteID,
        sessionUUID,
        isEdit,
        onValidate,
        showValidationErrors,
        authHeader,
        MLAlertService,
        isPolicyChange,
        isModernProduct
    } = props;

    const translator = useTranslator();
    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const isQNB = appConfig.portalCode === 'QB';
    const { registerComponentValidation, onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const { refreshData } = useDataRefresh();
    const [coverages, setCoverages] = useState([]);
    const exposureType = _.get(underlyingPolicyVM, 'value.exposureType');
    const [prevPolicyType, setPrevPolicyType] = useState(_.get(underlyingPolicyVM, 'policyType.value'));
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { EndorsementService } = useDependencies('EndorsementService');
    const hasSyncedEditCoverages = useRef(false);
    const policyTypeAvailableValues = _.get(underlyingPolicyVM, 'policyType.aspects.availableValues',[]);

    const coverageInfo = useMemo(() => [
        {
            coverageName: 'Homeowners Liability',
            coverageCode: 'PUPHomeLiability_ML',
            warningOnChosenTerm: ['z85hksvpun0dc3vtqamvgnq5j79'],
            warningMessage: messages.hoWarning,
            title: messages.liability,
            helpText: isModernProduct ? translator(messages.hoCovHelpText_NewProduct) : translator(messages.hoCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            coverageName: 'Motorcycle Liability',
            coverageCode: 'PUP_ULMotoLiabilityCov_ML',
            warningOnChosenTerm: ['zo4hc63jb15jsccjsjobr8ftsf8', 'zobg8bimn80tj84nhjel63hi4n8', 'z0ehgu9gnl74jblc61jq1u39n0a', 'zehhqkv8dhd3fbnqvaglqaqurg9'],
            warningMessage: messages.mcWarning,
            title: messages.liability,
            helpText: isModernProduct ? translator(messages.mcCovHelpText_NewProduct) : translator(messages.mcCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            coverageName: 'Watercraft Liability',
            coverageCode: 'PUPWatercraftLiability_ML',
            warningOnChosenTerm: ['zgoiabm537j7mdsei0b0dunpgl8', 'zo8h89475kiovecfj62c3tvreq8', 'z3bhkabnn7bmb5pdvuv5rgd2n98', 'z8mje745jhm7c0js3nl5ge0ci79'],
            warningMessage: messages.wcWarning,
            title: messages.liability,
            helpText: isModernProduct ? translator(messages.wcCovHelpText_NewProduct) : translator(messages.wcCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            coverageName: 'Recreational Vehicle Liability',
            coverageCode: 'PUPRecVehLiability_ML',
            warningOnChosenTerm: ['z0qhepmaopl5o7u2rmnr0q94nba', 'ze8i42q1nofdedpnnirrdoiu30a','zjsiicbeja51qc33bfcheof8flb', 'z1ai23ntp38dr08k6l8k1lbgni9'],
            warningMessage: messages.rvWarning,
            title: messages.liability,
            helpText: isModernProduct ? translator(messages.rvCovHelpText_NewProduct) : translator(messages.rvCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            coverageName: 'Automobile Liability',
            coverageCode: 'PUPAutoLiability_ML',
            warningMessage: messages.paWarning,
            warningOnChosenTerm: ['zbvhc3v558ln8d8616j9bkka27b', 'zfgji7jcdeceqcac6jvh5udqn08', 'z4kgqu1cln88m7cg9eb0281cvl9','z75huumlfqstb9u80csstrr0se9', 'zanjuerjm5g5a16klm64omse158','zo3hopuvhd9vkdirmgjbvkff4jb', 'zkdieqnkjtvai7b2ehdm5nhl2qb', 'z0fjc7e5acj69fa053t7pefs9l8'],
            title: messages.liability,
            helpText: isModernProduct ? translator(messages.paCovHelpText_NewProduct) : translator(messages.paCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            coverageName: 'Uninsured & Underinsured Motorist BI',
            coverageCode: 'PUPUninsuredAndUnderInsuredMotoristBI_ML',
            warningOnChosenTerm: [],
            warningMessage: null,
            title: messages.uninsuredAndUnderInsured,
            helpText: translator(messages.uninsuredAndUnderInsuredCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            coverageName: 'Underinsured Motorist Bodily Injury',
            coverageCode: 'PUPUnderinsuredMotoristBI_ML',
            warningOnChosenTerm: [],
            warningMessage: null,
            title: messages.underInsured,
            helpText: translator(messages.underInsuredCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        }
    ], [isModernProduct, translator]);

    const termInfo = useMemo(() => [
        {
            termName: 'Liability Limit',
            title: messages.liability,
            helpText: isModernProduct ? translator(messages.paCovHelpText_NewProduct) : translator(messages.paCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            termName: 'Uninsured Motorists Bodily Injury Limit',
            title: messages.uninsured,
            helpText: translator(messages.uninsuredCovHelpText, { lessThan: '<', greaterThan: '>', br: (<br/>) })
        },
        {
            termName: 'Uninsured Motorist Limit',
            title: messages.uninsured,
            helpText: translator(messages.uninsuredCovHelpText_NewProduct)
        }
    ], [isModernProduct, translator]);

    const hasMissingCoverageTermValue = (coverage) => {
        let hasMissingTermValue = false;
        coverage.terms.forEach((term) => {
            if (_.isNil(term.value)) {
                hasMissingTermValue = true;
            }
        });
        return hasMissingTermValue;
    };

    const checkForCoverageErrors = useCallback((policyCoverages) => {
        let errorFound = false;
        policyCoverages.forEach((cov) => {
            if (hasMissingCoverageTermValue(cov) && _.get(underlyingPolicyVM, 'isExternal.value', false)) {
                errorFound = true;
            }
        });
        return errorFound;
    }, [underlyingPolicyVM]);

    const updateCoveragesError = useCallback(() => {
        _.set(underlyingPolicyVM, 'coveragesError', checkForCoverageErrors(coverages));
    }, [checkForCoverageErrors, coverages, underlyingPolicyVM]);

    const processCoverages = useCallback(() => {
        const ulCoverages = _.get(underlyingPolicyVM, 'ulPolicyCoverage.coverages.value', []);
        if (ulCoverages.length > 0) {
            setCoverages(ulCoverages.map(_.partial(Coverage.create, _, false, null)));
            updateCoveragesError();
        }
    }, [underlyingPolicyVM, updateCoveragesError]);

    const validateExpirationDate = useCallback(() => {
        const effectiveDate = _.get(underlyingPolicyVM, 'effectiveDate.value');
        const expirationDate = _.get(underlyingPolicyVM, 'expirationDate.value');
        if (effectiveDate && expirationDate) {
            if (MLLocalDateUtil.compare(effectiveDate, expirationDate) > 0) {
                return false;
            }
        }
        return true;
    }, [underlyingPolicyVM]);

    useEffect(() => {
        registerComponentValidation(validateExpirationDate);
    }, [registerComponentValidation, validateExpirationDate]);

    const createUnderlyingPolicyRequest = (policyType) => {
        return {
            quoteID,
            policyType: policyType,
            underlyingPolicy: _.get(underlyingPolicyVM, 'value'),
            sessionUUID
        };
    };

    const createUnderlyingPolicyType = (policyType) => {
        const request = createUnderlyingPolicyRequest(policyType);
        const promise = MLUnderlyingPolicyService.createUnderlyingPolicy(request, authHeader);
        promise.then((data) => {
            _.set(underlyingPolicyVM, 'value', new UnderlyingPolicy(data));
            _.set(underlyingPolicyVM, 'partialAdditionFlag.value', false);
            _.set(underlyingPolicyVM, 'value.exposureType', exposureType);
            processCoverages();
        });
        promise.catch((error) => {
            console.error(error);
        });
    };

    const removeAndCreateUnderlyingPolicyType = (policyType) => {
        // partial addition flag set to true turns off fields requiredness
        _.set(underlyingPolicyVM, 'partialAdditionFlag.value', true);
        const request = createUnderlyingPolicyRequest(policyType);
        const promise = MLUnderlyingPolicyService.removeUnderlyingPolicy(request, authHeader);
        promise.then(() => {
            createUnderlyingPolicyType(policyType);
        });
        promise.catch((error) => {
            console.error(error);
        });
    };

    const hasWarningOnChosenTerm = (liabilityLimitsCovValue, chosenTermValues) => {
        if (!liabilityLimitsCovValue) {
            return false;
        }

        // Checking all coverage terms but most likely only 1 term
        const filteredArray = liabilityLimitsCovValue.terms.filter((term) => {
            return chosenTermValues.indexOf(term.chosenTerm) > -1;
        });

        return filteredArray.length > 0;
    };

    const getWarningMessagesOnLiabilityLimits = useCallback((underlyingPolicy) => {
        if (!underlyingPolicy.policyType.value || !underlyingPolicy.ulPolicyCoverage.value) {
            return '';
        }

        const covs = underlyingPolicy.ulPolicyCoverage.coverages.value;

        for (let i = 0; i < covs.length; i += 1) {
            const cov = covs[i];
            const covInfo = coverageInfo.find((info) => info.coverageCode === cov.code_ML);

            if (covInfo && hasWarningOnChosenTerm(cov, covInfo.warningOnChosenTerm)) {
                return covInfo.warningMessage;
            }
        }
        return '';
    }, []);

    const hasWarningOnLiabilityLimits = useCallback((underlyingPolicy) => {
        return getWarningMessagesOnLiabilityLimits(underlyingPolicy) !== '';
    }, [getWarningMessagesOnLiabilityLimits]);

    const showAlerts = useCallback((underlyingPolicy) => {
        MLAlertService.clearWarningAlerts();
        if (hasWarningOnLiabilityLimits(underlyingPolicy)) {
            const liabilityMessage = getWarningMessagesOnLiabilityLimits(underlyingPolicy);
            const warningMsg = translator(messages.warningMessage, { liabilityMessage: translator(liabilityMessage) });
            MLAlertService.addWarningAlert(warningMsg)
        }
    }, [MLAlertService, getWarningMessagesOnLiabilityLimits, hasWarningOnLiabilityLimits, translator]);

    const onModelChange = () => {
        refreshData();
        getWarningMessagesOnLiabilityLimits(underlyingPolicyVM);
    };

    const onCoverageTermUpdate = () => {
        updateCoveragesError();
        showAlerts(underlyingPolicyVM);
    }

    const syncUnderlyingPolicyCoverages = useCallback( async (ulPolicyVM) => {
        const policyCov = ulPolicyVM.ulPolicyCoverage.value;
        const ulPolicyCoverages = [policyCov];

        const ulPolCovData = {
            'personalUmbrella': {
                'ulPolicyCoverages': ulPolicyCoverages
            }
        };

        if (isPolicyChange) {
            const policyChangeResponse = await EndorsementService.updateCoverages(quoteID, ulPolCovData, authHeader);
            console.log(policyChangeResponse);

            // Find index of underlying policy in response that we want to save to underlying policy
            const index = policyChangeResponse.lobData.personalUmbrella.underlyingPolicies.findIndex(ulPolicy => ulPolicy.publicID === ulPolicyVM.publicID.value);

            _.set(ulPolicyVM, 'ulPolicyCoverage.coverages.value', policyChangeResponse.lobData.personalUmbrella.underlyingPolicies[index].ulPolicyCoverage.coverages);
        } else {
            const response = await LoadSaveService.updateCoverages(
                quoteID,
                sessionUUID,
                ulPolCovData,
                authHeader
            );

            // find index of policy coverage in response that we want to save to underlying policy
            const index = response.personalUmbrella.ulPolicyCoverages.findIndex((polCov) => polCov.publicID === policyCov.publicID);

            _.set(ulPolicyVM, 'ulPolicyCoverage.coverages.value', response.personalUmbrella.ulPolicyCoverages[index].coverages);
        }

        processCoverages();
        
        updateCoveragesError();
        showAlerts(underlyingPolicyVM);
        onModelChange();

    }, [LoadSaveService, authHeader, onModelChange, processCoverages, quoteID, sessionUUID, showAlerts, underlyingPolicyVM, updateCoveragesError]);

    useEffect(() => {
        if (isEdit && !hasSyncedEditCoverages.current) {
            hasSyncedEditCoverages.current = true;
            syncUnderlyingPolicyCoverages(underlyingPolicyVM);
        }
    }, [isEdit, underlyingPolicyVM, syncUnderlyingPolicyCoverages]);

    const syncCoverageTerms = useCallback((shouldSyncCoverages = false,) => {
        if (shouldSyncCoverages) {
            syncUnderlyingPolicyCoverages(underlyingPolicyVM);
        } else {
            updateCoveragesError();
            showAlerts(underlyingPolicyVM);
            onModelChange();
        }
    }, [onModelChange, showAlerts, syncUnderlyingPolicyCoverages, underlyingPolicyVM, updateCoveragesError]);

    const generateCoverageTermsOverrides = () => {
        const overrides = coverages.map((cov, index) => {
            return {
                [`underlyingPolicyCoverageTermsComponent${index}`]: {
                    model: cov,
                    covIndex: index,
                    onModelChange,
                    onCoverageTermUpdate,
                    onValidate,
                    showValidationErrors,
                    coverageInfo,
                    termInfo,
                    isExternal: _.get(underlyingPolicyVM, 'isExternal.value'),
                    isEdit,
                    isModernProduct,
                    syncCoverageTerms
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const formatInsuredOptions = (existingNamedInsureds) => {
        if (!existingNamedInsureds) return [];
        return existingNamedInsureds.map((insured) => ({
            code: insured.ulRole,
            name: translator({
                id: insured.displayName,
                defaultMessage: insured.displayName
            })
        }));
    };

    const getExpirationDateValidationMessages = () => {
        if (!validateExpirationDate()) {
            return [translator(messages.expirationDateError)];
        }
        return _.get(underlyingPolicyVM, 'expirationDate.aspects.validationMessages');
    };

    const isExternal = () => {
        return _.get(underlyingPolicyVM, 'isExternal.value', false);
    }

    const isFarmersCarrier = () => {
        return _.get(underlyingPolicyVM, 'carrier.value.code') === 'farmers_insurance_group_of_cos_ml';
    }

    const isOtherCarrier = () => {
        return _.get(underlyingPolicyVM, 'carrier.value.code') === 'Other';
    };

    const isOtherInsured = () => {
        return _.get(underlyingPolicyVM, 'namedInsured.value.code') === 'other';
    };

    const isPA = () => {
        return _.get(underlyingPolicyVM, 'policyType.value.code') === 'PA';
    };

    const getNamedNonOwnerHelpText = () => {
        return (isAMP || isQNB) ? translator(messages.namedNonOwnerHelpText) : '';
    };

    const getNamedNonOwnerHelpTextTitle = () => {
        return (isAMP || isQNB) ? translator(messages.namedNonOwnerHelpTextTitle) : '';
    };

    useEffect(() => {
        _.set(underlyingPolicyVM, 'coveragesError', false);
        _.set(underlyingPolicyVM, 'partialAdditionFlag.value', !isEdit);
        const potentialContacts = _.get(underlyingPolicyVM, 'existingNamedInsureds.value');
        _.set(underlyingPolicyVM, 'value.contact', getContactByTypeCode(potentialContacts, _.get(underlyingPolicyVM, 'namedInsured.value')));
        _.set(underlyingPolicyVM, 'policyNumber', getPolicyNumber(_.get(underlyingPolicyVM, 'policyNumber.value')));
        processCoverages();
    }, []);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const getSubcompanies = () => {
        const subCompaniesTypeList = underlyingPolicyVM?.subCompanies?.aspects.availableValues;
        if (_.isEmpty(subCompaniesTypeList) || _.isUndefined(underlyingPolicyVM.policyType.value)) {
            return [];
        }
        const types = subCompaniesTypeList.filter((subcompany) => {
            return subcompany.belongsToCategory(underlyingPolicyVM.policyType.value);
        });

        const typecodes = types.map((type) => ({
            code: type.code,
            name: translator({
                id: type.name,
                defaultMessage: type.name
            })
        }));
        return typecodes;
    };

    const policyTypeOptions = policyTypeAvailableValues.map((option)=> ({
        code: option.code,
        name: isModernProduct && option?.code === 'HO' 
            ? 'Property' 
            : translator({
                id: option?.name,
                defaultMessage: option?.name
            })
    }));

    const overrideProps = {
        policyTypeTooltipContainer: {
            className: classNames(styles.tooltipContainerOverride, styles.policyTypeTooltipContainer, isAMP ? 'col-md-6' : 'col-md-8')
        },
        policyTypeTooltip: {
            tooltipLabel: messages.policyTypeTooltipLabel,
            tooltipContent: translator(messages.policyTypeTooltipContent, { br: (<br/>) }),
            tooltipContentClassName: styles.underlyingPolicyTooltip
        },
        policyTypeContainer: {
            className: classNames(styles.policyTypeContainer, isAMP ? 'col-md-6' : 'col-md-4')
        },
        policyTypeDropdownSelect: {
            availableValues: policyTypeOptions
        },
        isExternalContainer: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        insuredDropdownSelect: {
            availableValues: formatInsuredOptions(_.get(underlyingPolicyVM, 'existingNamedInsureds.value'))
        },
        coveragesIterable: {
            data: coverages
        },
        carrierNameRow: {
            visible: isExternal(),
        },
        carrierNameContainer: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        carrierNameDropdownSelect: {
            disabled: _.isNil(_.get(underlyingPolicyVM, 'policyType.value'))
        },
        subCompanyRow: {
            visible: !isExternal() || isFarmersCarrier()
        },
        subCompanyContainer: {
            className: isAMP ? 'col-md-7' : 'col-md-5'
        },
        subCompanyDropdownSelect: {
            disabled: _.isNil(_.get(underlyingPolicyVM, 'policyType.value')),
            availableValues: getSubcompanies(),
            required: true
        },
        otherCarrierNameRow: {
            visible: isExternal() && isOtherCarrier()
        },
        otherCarrierNameContainer: {
            className: isAMP ? 'col-md-7' : 'col-md-5'
        },
        policyNumberContainer: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        insuredContainer: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        otherInsuredRow: {
            visible: isOtherInsured()
        },
        otherInsuredContainer: {
            className: isAMP ? 'col-md-7' : 'col-md-5'
        },
        effectiveDateContainer: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        expirationDateContainer: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        expirationDateInput: {
            validationMessages: getExpirationDateValidationMessages()
        },
        isNonOwnerRow: {
            visible: isPA() && !isModernProduct
        },
        isNonOwnerRadio: {
            required: isPA() && !isModernProduct
        },
        isNonOwnerTooltipContainer: {
            visible: !isGPA
        },
        isNonOwnerTooltip: {
            tooltipLabel: translator(getNamedNonOwnerHelpTextTitle()),
            tooltipContent: translator(getNamedNonOwnerHelpText()),
            noWrapper: true
        },
        ...generateCoverageTermsOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            underlyingPolicyCoverageTermsComponent: UnderlyingPolicyCoverageTermsComponent
        },
        resolveClassNameMap: styles
    };

    const onPolicyTypeUpdate = (value) => {
        _.set(underlyingPolicyVM, 'carrier.value', undefined);
        if (value) {
            if (!_.isNil(prevPolicyType)) {
                removeAndCreateUnderlyingPolicyType(value);
            } else {
                createUnderlyingPolicyType(value);
            }
        }
        setPrevPolicyType(value);
    };

    const updateCarrier = () => {
        _.set(underlyingPolicyVM, 'carrier.value', 'farmers_insurance_group_of_cos_ml');
    };

    const onValueChange = (value, path) => {
        switch (path) {
            case 'policyType': {
                onPolicyTypeUpdate(value);
                break;
            }
            case 'subCompanies': {
                updateCarrier();
                break;
            }
            default:
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            overrideProps={overrideProps}
            model={underlyingPolicyVM}
            onValueChange={onValueChange}
            onModelChange={onModelChange}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
        />
    );
}

UnderlyingPolicyComponent.propTypes = {
    id: PropTypes.string.isRequired,
    underlyingPolicyVM: PropTypes.shape({}).isRequired,
    quoteID: PropTypes.string.isRequired,
    sessionUUID: PropTypes.string,
    showValidationErrors: PropTypes.bool.isRequired,
    isEdit: PropTypes.bool.isRequired,
    onValidate: PropTypes.func.isRequired,
    MLAlertService: PropTypes.func.isRequired,
    isModernProduct: PropTypes.bool
};

UnderlyingPolicyComponent.defaultProps = {
    isModernProduct: false
}

export default UnderlyingPolicyComponent;
